import { tracker } from './SessionRecorder';
import { setAnnotatorVisible } from './SessionAnnotator';

// Per-tab flag: set while the user has recording on, so a page reload can
// resume it (the recorder keeps the same session across reloads, but the
// on-demand host never auto-starts, so we must re-arm it ourselves).
const ACTIVE_KEY = 'tracium_ondemand_active';
// Remembered dock position (drag), persisted across reloads/sessions.
const POS_KEY = 'tracium_dock_pos';
const DOCK_ID = 'tracium-rec-dock';
const BUTTON_ID = 'tracium-record-btn';
const CANCEL_ID = 'tracium-cancel-btn';
const DRAG_THRESHOLD = 4;

let dockEl: HTMLDivElement | null = null;
let buttonEl: HTMLButtonElement | null = null;
let cancelEl: HTMLButtonElement | null = null;

// Drag state. `didDrag` distinguishes a drag from a click so moving the dock
// doesn't also start/stop recording.
let dragging = false;
let didDrag = false;
let startX = 0;
let startY = 0;
let originLeft = 0;
let originTop = 0;

const wantsRecording = (): boolean => {
	try { return window.sessionStorage.getItem(ACTIVE_KEY) === '1'; } catch { return false; }
};

const setWantsRecording = (on: boolean): void => {
	try {
		if (on) window.sessionStorage.setItem(ACTIVE_KEY, '1');
		else window.sessionStorage.removeItem(ACTIVE_KEY);
	} catch { /* sessionStorage unavailable — ignore */ }
};

const savePos = (left: number, top: number): void => {
	try { window.localStorage.setItem(POS_KEY, JSON.stringify({ left, top })); } catch { /* ignore */ }
};

const loadPos = (): { left: number; top: number } | null => {
	try {
		const raw = window.localStorage.getItem(POS_KEY);
		if (!raw) return null;
		const p = JSON.parse(raw);
		if (typeof p?.left === 'number' && typeof p?.top === 'number') return { left: p.left, top: p.top };
	} catch { /* ignore */ }
	return null;
};

// Move the dock to an absolute position, clamped inside the viewport, switching
// from the default bottom/right anchoring to left/top.
const applyPos = (left: number, top: number): void => {
	if (!dockEl) return;
	const w = dockEl.offsetWidth || 160;
	const h = dockEl.offsetHeight || 40;
	const l = Math.max(0, Math.min(window.innerWidth - w, left));
	const t = Math.max(0, Math.min(window.innerHeight - h, top));
	dockEl.style.left = `${l}px`;
	dockEl.style.top = `${t}px`;
	dockEl.style.right = 'auto';
	dockEl.style.bottom = 'auto';
};

const onDragStart = (e: PointerEvent): void => {
	if (!dockEl || e.button !== 0) return;
	const rect = dockEl.getBoundingClientRect();
	originLeft = rect.left;
	originTop = rect.top;
	startX = e.clientX;
	startY = e.clientY;
	dragging = true;
	didDrag = false;
	window.addEventListener('pointermove', onDragMove);
	window.addEventListener('pointerup', onDragEnd, { once: true });
};

const onDragMove = (e: PointerEvent): void => {
	if (!dragging || !dockEl) return;
	const dx = e.clientX - startX;
	const dy = e.clientY - startY;
	if (!didDrag && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
		didDrag = true;
		dockEl.setAttribute('data-dragging', 'true');
	}
	if (didDrag) applyPos(originLeft + dx, originTop + dy);
};

const onDragEnd = (): void => {
	window.removeEventListener('pointermove', onDragMove);
	dragging = false;
	if (dockEl) dockEl.removeAttribute('data-dragging');
	if (didDrag && dockEl) {
		const rect = dockEl.getBoundingClientRect();
		savePos(rect.left, rect.top);
	}
	// didDrag stays true until the click handler consumes it, then the next
	// pointerdown resets it.
};

const render = (): void => {
	if (!buttonEl) return;
	const recording = tracker.isRecording();

	const icon = document.createElement('span');
	icon.className = recording ? 'tracium-rec-dot' : 'tracium-rec-tri';
	const label = document.createElement('span');
	label.textContent = recording ? 'Recording — stop' : 'Record session';

	buttonEl.textContent = '';
	buttonEl.appendChild(icon);
	buttonEl.appendChild(label);
	buttonEl.style.background = recording ? '#dc2626' : '#111827';
	buttonEl.setAttribute('aria-pressed', recording ? 'true' : 'false');
	buttonEl.title = recording
		? 'Session recording is ON — click to stop and save (drag to move)'
		: 'Start session recording on demand (drag to move)';

	// The cancel/discard button only makes sense while recording.
	if (cancelEl) cancelEl.style.display = recording ? 'inline-flex' : 'none';
};

const onClick = (): void => {
	if (didDrag) return; // this press was a drag, not a click
	if (tracker.isRecording()) {
		tracker.stop();
		setWantsRecording(false);
	} else {
		tracker.startOnDemand();
		setWantsRecording(tracker.isRecording());
	}
	setAnnotatorVisible(tracker.isRecording());
	render();
};

const onCancel = (): void => {
	if (didDrag) return; // this press was a drag, not a click
	if (!tracker.isRecording()) return;
	// Deleting is irreversible — confirm first.
	if (!window.confirm('Discard this recording? The session and everything captured so far will be deleted.')) return;
	tracker.cancel();
	setWantsRecording(false);
	setAnnotatorVisible(false);
	render();
};

const injectStyles = (): void => {
	if (document.getElementById('tracium-record-btn-styles')) return;
	const style = document.createElement('style');
	style.id = 'tracium-record-btn-styles';
	style.textContent = `
#${DOCK_ID}{position:fixed;bottom:16px;right:16px;z-index:2147483000;display:flex;
	align-items:center;gap:8px;touch-action:none;}
#${DOCK_ID}[data-dragging="true"]{cursor:grabbing;}
#${BUTTON_ID},#${CANCEL_ID}{display:inline-flex;align-items:center;gap:8px;padding:9px 15px;
	border:none;border-radius:9999px;color:#fff;font:600 13px/1 system-ui,-apple-system,Segoe UI,sans-serif;
	cursor:pointer;box-shadow:0 2px 12px rgba(0,0,0,.35);transition:background .15s ease,transform .1s ease;}
#${CANCEL_ID}{display:none;background:#4b5563;}
#${BUTTON_ID}:hover,#${CANCEL_ID}:hover{transform:translateY(-1px);}
#${CANCEL_ID}:hover{background:#6b7280;}
#${DOCK_ID}[data-dragging="true"] #${BUTTON_ID},#${DOCK_ID}[data-dragging="true"] #${CANCEL_ID}{cursor:grabbing;}
#${BUTTON_ID} .tracium-rec-tri{width:0;height:0;border-style:solid;border-width:6px 0 6px 10px;
	border-color:transparent transparent transparent #fff;display:inline-block;}
#${BUTTON_ID} .tracium-rec-dot{width:11px;height:11px;border-radius:50%;background:#fff;
	display:inline-block;animation:tracium-rec-pulse 1.2s ease-in-out infinite;}
@keyframes tracium-rec-pulse{0%,100%{opacity:1;transform:scale(1);}50%{opacity:.35;transform:scale(.8);}}
`;
	document.head.appendChild(style);
};

/**
 * Mount the on-demand Record + Discard buttons in a draggable dock. No-op
 * unless we're on the on-demand host. Safe to call more than once. If a
 * recording was active before a reload, re-arms it so it continues seamlessly.
 */
export const mountSessionRecorderButton = (): void => {
	if (!tracker.isOnDemandHost()) return;

	if (typeof document === 'undefined') return;
	if (!document.body) {
		document.addEventListener('DOMContentLoaded', mountSessionRecorderButton, { once: true });
		return;
	}
	if (document.getElementById(DOCK_ID)) return;

	// Resume a recording that was on before this reload.
	if (wantsRecording() && !tracker.isRecording()) {
		tracker.startOnDemand();
		setWantsRecording(tracker.isRecording());
	}

	injectStyles();

	dockEl = document.createElement('div');
	dockEl.id = DOCK_ID;
	dockEl.addEventListener('pointerdown', onDragStart);

	cancelEl = document.createElement('button');
	cancelEl.id = CANCEL_ID;
	cancelEl.type = 'button';
	cancelEl.title = 'Discard & delete this recording (nothing is saved)';
	cancelEl.textContent = '✕ Discard';
	cancelEl.addEventListener('click', onCancel);
	dockEl.appendChild(cancelEl);

	buttonEl = document.createElement('button');
	buttonEl.id = BUTTON_ID;
	buttonEl.type = 'button';
	buttonEl.addEventListener('click', onClick);
	dockEl.appendChild(buttonEl);

	document.body.appendChild(dockEl);

	// Restore a saved drag position (clamped to the current viewport).
	const pos = loadPos();
	if (pos) applyPos(pos.left, pos.top);

	// Keep the dock on-screen if the window is resized.
	window.addEventListener('resize', () => {
		if (dockEl && dockEl.style.left) applyPos(parseFloat(dockEl.style.left), parseFloat(dockEl.style.top));
	});

	// Reflect a resumed recording — show the annotation toolbar if we're recording.
	setAnnotatorVisible(tracker.isRecording());
	render();
};
